import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { NetworkError } from "../../../../api";
import { AUTH_MESSAGES, newAuthRequest, pinError } from "../../../../api/newAuthorization";
import { ESMSFormState } from "../../../../store/slices/smsFormSliceCreator";
import { LoginFormState, useLoginFormStore } from "../../../../store/loginFormStore";
import { EPassportFormState } from "../../../../store/slices/passportFormSliceCreator";
import { EPinState, IPinFormState, usePinFormStore } from "../../../../store/pinFormStore";
import { useAuthorizationStore, isNewAuthorization } from "../../../../store/authorizationStore";
import type { AuthorizationState, AuthorizationStep } from "./types";
import { verifySms } from "./verifySms";
import { submitPin } from "./submitPin";
import { verifyPassport } from "./verifyPassport";

function handleStepError(step: AuthorizationStep, error: unknown, auth: AuthorizationState, login: LoginFormState, pin: IPinFormState) {
  switch (step) {
    case "sms":
      login.setErrorMessage(error instanceof NetworkError && error.status === 461 && error.internalCode === 7001 ? AUTH_MESSAGES.incorrect : AUTH_MESSAGES.sms);
      login.setSMSFormState(ESMSFormState.initial);
      break;
    case "passport":
      login.setTextError(AUTH_MESSAGES.passport);
      login.setFormState(EPassportFormState.initial);
      break;
    case "pin":
      pin.setErrorMessage(auth.restoringPin || auth.flow === "REG" ? AUTH_MESSAGES.pin : pinError(error));
      pin.setState(EPinState.Initial);
      break;
  }
}

export function useNewAuthorization(step: AuthorizationStep) {
  const login = useLoginFormStore();
  const pin = usePinFormStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const requestRef = useRef<AbortController>();
  const epoch = useAuthorizationStore(state => state.epoch);

  useEffect(() => {
    if (!isNewAuthorization()) {
      return;
    }

    const auth = useAuthorizationStore.getState();
    const currentLogin = useLoginFormStore.getState();
    const currentPin = usePinFormStore.getState();

    switch (step) {
      case "sms":
        if (currentLogin.smsFormState !== ESMSFormState.validated) {
          return;
        }
        login.setSMSFormState(ESMSFormState.submitting);
        break;
      case "passport":
        if (currentLogin.formState !== EPassportFormState.validated) {
          return;
        }
        login.setFormState(EPassportFormState.submitting);
        break;
      case "pin":
        if (currentPin.state !== EPinState.Validated) {
          return;
        }
        pin.setState(EPinState.Submitting);
        break;
    }

    const controller = new AbortController();
    const isCurrent = () => !controller.signal.aborted && useAuthorizationStore.getState().epoch === auth.epoch && isNewAuthorization();
    const request = (path: string, body: object, method = "POST") => newAuthRequest(path, body, method, undefined, controller.signal);
    const context = { request, isCurrent, navigate };

    const run = async () => {
      try {
        switch (step) {
          case "sms":
            await verifySms({
              ...context,
              auth: { otpSessionUuid: auth.otpSessionUuid, restoringPin: auth.restoringPin },
              login: { smsCode: login.smsCode, setSMSFormState: login.setSMSFormState },
            });
            break;
          case "passport":
            await verifyPassport({
              ...context,
              auth: { authSessionId: auth.authSessionId, foreignPassport: auth.foreignPassport },
              login: { getEscapedPassportNumber: login.getEscapedPassportNumber, passportType: login.passportType, setFormState: login.setFormState },
            });
            break;
          case "pin":
            await submitPin({
              ...context,
              auth: {
                authSessionId: auth.authSessionId,
                deviceId: auth.deviceId,
                restoringPin: auth.restoringPin,
                flow: auth.flow,
                passportChecked: auth.passportChecked,
              },
              login: { clearPhoneForm: login.clearPhoneForm, clearSMSForm: login.clearSMSForm, clearForm: login.clearForm },
              pin: { pin: pin.pin, reset: pin.reset },
            });
            break;
        }
      } catch (error) {
        if (isCurrent()) {
          handleStepError(step, error, auth, login, pin);
        }
      }
    };

    requestRef.current = controller;
    void run();
    // Form state changes must not cancel requests; the lifecycle effect handles cancellation.
  }, [login.smsFormState, login.formState, pin.state, epoch]);

  useEffect(() => () => {
    requestRef.current?.abort();
    if (useAuthorizationStore.getState().epoch !== epoch) {
      return;
    }

    const currentLogin = useLoginFormStore.getState();
    const currentPin = usePinFormStore.getState();

    if (step === "sms" && currentLogin.smsFormState === ESMSFormState.submitting) {
      currentLogin.setSMSFormState(ESMSFormState.initial);
    }
    if (step === "passport" && currentLogin.formState === EPassportFormState.submitting) {
      currentLogin.setFormState(EPassportFormState.initial);
    }
    if (step === "pin" && currentPin.state === EPinState.Submitting) {
      currentPin.reset();
    }
  }, [pathname, epoch, step]);
}
